import React, { useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

export const UPDATE_READY = 'app:update-ready';

// An update that lands while the app is still starting is applied without asking. The service
// worker serves the previous build from its cache until a new one takes over, so a phone that
// installed the app yesterday opens yesterday's app - and a notice offering to reload is easy to
// miss and easy to read as unrelated to the feature that appears to be missing. Nothing has been
// typed this early, so there is nothing a reload can lose.
const LOADED_AT = Date.now();
const STARTUP_MS = 20000;

/**
 * A new build reaches the browser as a service worker that installs and then waits,
 * because the one already running keeps control until every tab using it closes. Left
 * alone that means a deploy silently never arrives: the app keeps serving the previous
 * bundle across reloads, and a fix looks like it was never made.
 *
 * So a waiting worker is taken over at once while the app is still starting, and surfaced as a
 * notice after that — a reload is never forced in the middle of something the user is doing.
 */
const UpdatePrompt = () => {
  const [waiting, setWaiting] = useState(null);

  // The page reloads once the new worker has actually taken over, rather than immediately —
  // reloading first would just re-serve the cached old bundle.
  const apply = (worker) => {
    navigator.serviceWorker?.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    worker.postMessage({ type: 'SKIP_WAITING' });
  };

  useEffect(() => {
    const onReady = (event) => {
      const worker = event.detail?.waiting || null;
      if (!worker) { setWaiting(null); return; }
      if (Date.now() - LOADED_AT < STARTUP_MS) { apply(worker); return; }
      setWaiting(worker);
    };
    window.addEventListener(UPDATE_READY, onReady);
    return () => window.removeEventListener(UPDATE_READY, onReady);
  }, []);

  const reload = () => {
    if (!waiting) { window.location.reload(); return; }
    apply(waiting);
  };

  return (
    <Snackbar open={!!waiting} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        action={<Button color="inherit" size="small" onClick={reload}>Reload</Button>}
      >
        A new version is available.
      </Alert>
    </Snackbar>
  );
};

export default UpdatePrompt;
